import { CountryApproverData } from 'api/travel-request-detail';
import { autoinject, computedFrom } from 'aurelia-framework';
import { watch } from 'aurelia-watch-decorator';
import { WidgetController } from 'controls/as-widget-container';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { EventHelper, Subscription } from 'framework/aurelia_tools/event-helper';
import { ApprovalStatus } from 'model/business-enums';
import { Country } from 'model/business-keywords';
import { WidgetBase } from 'support/base_classes/widget-base';
import { ApplicationAsyncMessage, ApplicationMessage } from 'support/messaging';

@autoinject
export class CountryApproverWidget extends WidgetBase<{ country: Country; approver: CountryApproverData }, WidgetController> {
  comment: string = null;
  siblingEditing = false;
  private siblingEditingSubscription: Subscription = null;

  constructor(private dialogHelper: DialogHelper, private eventHelper: EventHelper) {
    super();
  }

  bind() {
    this.comment = this.model.approver.comment ?? null;
  }

  attached() {
    this.siblingEditingSubscription = this.eventHelper.subscribe(ResponseEditing, e => e.sender !== this && (this.siblingEditing = e.editing));
  }

  detached() {
    this.siblingEditingSubscription.dispose();
  }

  @computedFrom('model.country.formFlag')
  get widgetIcon() {
    return this.model.country.formFlag ?? 'fas fa-globe-africa';
  }

  @computedFrom('model.approver.status')
  get canPost() {
    return this.model.approver.status?.value === ApprovalStatus.Pending;
  }

  @computedFrom('model.approver.status')
  get canUndo() {
    return this.model.approver.status?.value != null && this.model.approver.status.value !== ApprovalStatus.Pending;
  }

  @computedFrom('canPost', 'canUndo')
  get buttons() {
    return [
      { statusValue: ApprovalStatus.Approved, name: 'Approve', buttonClass: 'btn-success', show: this.canPost },
      { statusValue: ApprovalStatus.Declined, name: 'Decline', buttonClass: 'btn-danger', show: this.canPost },
      { statusValue: ApprovalStatus.RequireMoreInfo, name: 'Request Info', buttonClass: 'btn-info', show: this.canPost },
      { statusValue: ApprovalStatus.Pending, name: 'Undo', buttonClass: 'btn-light', iconClass: 'fas fa-undo', show: this.canUndo }
    ].filter(b => b.show);
  }

  getStatusClass(statusValue: ApprovalStatus) {
    return statusValue != null ? `--${ApprovalStatus[statusValue].toLowerCase()}` : null;
  }

  @computedFrom('comment')
  get editing() {
    return this.comment !== (this.model.approver.comment ?? null);
  }

  @watch((x: CountryApproverWidget) => x.editing)
  private editingChanged(editing: boolean) {
    this.eventHelper.publish(new ResponseEditing(this, editing));
  }

  async submit(statusValue: ApprovalStatus) {
    if (!this.siblingEditing || await this.dialogHelper.confirm({ dialogTitle: 'Loose other changes?', message: 'You haven\'t submitted changes made to comments of other approvers. If you proceed, these changes will be lost!', okTitle: 'I can handle it', cancelTitle: 'Cancel' })) {
      const message = new ResponseSubmitted(this, this.model.country, this.model.approver, statusValue, this.comment);
      this.eventHelper.publish(message);
      if (message.handlerPromise)
        await message.handlerPromise;
    }
  }
}

export class ResponseEditing extends ApplicationMessage<CountryApproverWidget> {
  constructor(sender: CountryApproverWidget, public editing: boolean) {
    super(sender);
  }
}

export class ResponseSubmitted extends ApplicationAsyncMessage<CountryApproverWidget, unknown> {
  constructor(sender: CountryApproverWidget, public country: Country, public approver: CountryApproverData, public statusValue: ApprovalStatus, public comment: string) {
    super(sender);
  }
}
